/**
 * 분할 보기 v5 — 긴호흡 문단 행 정렬(docs/SPLITVIEW_ROWS_DESIGN.md D4-1).
 *
 * `CaptionFeed` 를 확장하지 **않는다**. 행 모델은 (a) 가상화 단위가 항목→행
 * 으로 바뀌고 (b) 앵커 좌표계가 행 id 로 바뀌고 (c) `unseen` 분모가 달라진다
 * — 핵심 3축이 전부 다르다. 프롭 하나를 더 얹으면 그 파일이 두 컴포넌트를
 * 겸하게 되고, v4 가 어렵게 고정한 "`kinds` 미지정이면 바이트 동일" 계약이
 * 네 번째 분기 아래에서 조용히 깨진다.
 *
 * **단, 카드 렌더는 공유한다** — 행 셀 안에서 `CaptionCard` 를 직접 부른다.
 * `CaptionFeed` 를 중첩하면 스크롤러·가상화기가 행마다 생긴다.
 *
 * 스크롤러가 하나뿐이므로 `role="region"` 도 하나이고(D3-2), `aria-live` 도
 * 하나다 — v4 D6-2 의 "둘 다 live 면 중복 낭독" 문제가 구조적으로 사라진다.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Empty } from '@cupia/ui/empty'
import { MessagesSquare } from 'lucide-react'
import type { FeedItem, FinalItem } from '../lib/reducer'
import { DEFAULT_ORDER } from '../lib/feed-order'
import { buildRows, indexMembers, type FeedRow } from '../lib/split-rows'
import { useOrphanClock } from '../lib/use-orphan-clock'
import { CaptionCard, clock } from './caption-card'
import type { CaptionFeedProps } from './caption-feed'
import { LiveWaiting } from './live-waiting'

export interface SplitRowsProps extends Omit<
  CaptionFeedProps,
  'kinds' | 'live' | 'emptyTitle' | 'emptyDescription'
> {
  emptyTitle?: string
  emptyDescription?: string
}

/**
 * 행 추정 높이 — 정확할 필요 없다(`measureElement` 가 확정한다).
 * 문단 본문 1줄 + 멤버 수 × 카드 추정치(D2-5).
 */
function estimateRowSize(row: FeedRow, presentation: boolean): number {
  const card = presentation ? 190 : 110
  const members = Math.max(row.left.length, row.paragraph ? 1 : 0)
  return Math.max(card, members * card)
}

/** 행 종류별 aria 라벨의 꼬리 — 눈금 시각과 함께 읽힌다(D3-2). */
function rowKindLabel(row: FeedRow): string {
  if (row.kind === 'para') return '문단'
  if (row.kind === 'orphan') return '문단 없음'
  return row.prev ? '이전 회의' : '대기 중'
}

export function SplitRows({
  items: rawItems,
  src,
  dst,
  showSource = true,
  veilSource = false,
  collapseInferred,
  collapseAssistant,
  onDelete,
  speakers,
  onRenameSpeaker,
  onLoadMore,
  presentation = false,
  order = DEFAULT_ORDER,
  emptyTitle = '아직 자막이 없습니다',
  emptyDescription = '발화가 감지되면 여기에 실시간으로 표시됩니다.',
  room,
  viewerKey,
  scrollState,
}: SplitRowsProps): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null)
  const orphanClock = useOrphanClock()

  const rows = useMemo(
    () => buildRows(rawItems, { now: orphanClock, order }),
    [rawItems, orphanClock, order],
  )

  /**
   * 앵커 폴백 좌표(D1-3) — **v4 대비 가장 큰 동작 변화**다.
   * v4 는 "분할 좌측은 흡수를 안 하므로 `paraOf` 폴백이 안 탄다" 고 적었지만
   * 행 모드에서는 그 문장이 무효다: 앵커 카드가 대기 행에 있다가 para 행으로
   * 이동하면 그 DOM 노드는 **다른 행 안으로** 옮겨간다. 폴백이 없으면 문단
   * 도착이 스크롤을 잡아챈다(e2e R2-2 가 이것을 고정한다).
   */
  const paraOf = useMemo(() => indexMembers(rawItems).paraOf, [rawItems])
  const paraOfRef = useRef(paraOf)
  paraOfRef.current = paraOf

  const newestAtTop = order === 'newest-first'

  const [atNewest, setAtNewest] = useState(
    () => scrollState?.initial?.atNewest ?? true,
  )
  const [unseen, setUnseen] = useState(() => scrollState?.initial?.unseen ?? 0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [exhausted, setExhausted] = useState(false)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const row = rows[index]
      return row
        ? estimateRowSize(row, presentation)
        : estimateRowSize(
            {
              kind: 'pending',
              id: '',
              rowSeq: null,
              ts: null,
              paragraph: null,
              left: [],
              prev: false,
            },
            presentation,
          )
    },
    overscan: 8,
    getItemKey: (index) => rows[index]?.id ?? index,
  })

  const stickToNewest = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = newestAtTop ? 0 : el.scrollHeight - el.clientHeight
  }, [newestAtTop])

  /* ── 스크롤 앵커링 — caption-feed.tsx 와 같은 기제, 좌표계만 행이다. ── */
  const anchorRef = useRef<{ id: string; offset: number } | null>(
    scrollState?.initial?.anchorId
      ? {
          id: scrollState.initial.anchorId,
          offset: scrollState.initial.anchorOffset,
        }
      : null,
  )
  const selfScrollRef = useRef(false)

  const captureAnchor = useCallback((): void => {
    const el = scrollRef.current
    if (!el) return
    const top = el.getBoundingClientRect().top
    for (const node of el.querySelectorAll<HTMLElement>('[data-item-id]')) {
      const rect = node.getBoundingClientRect()
      if (rect.bottom > top) {
        const id = node.dataset['itemId']
        if (id !== undefined)
          anchorRef.current = { id, offset: rect.top - top }
        return
      }
    }
    anchorRef.current = null
  }, [])

  const restoreAnchor = useCallback((): void => {
    const el = scrollRef.current
    const anchor = anchorRef.current
    if (!el || !anchor) return
    let node = el.querySelector<HTMLElement>(
      `[data-item-id="${CSS.escape(anchor.id)}"]`,
    )
    if (!node) {
      // D1-3 폴백 — 앵커가 카드 id 였고 그 카드가 para 행으로 이동했다.
      // `paraOf` 로 그 문단을 찾아 **행 id** 로 해석한다.
      const paraId = paraOfRef.current.get(anchor.id)
      if (paraId) {
        node = el.querySelector<HTMLElement>(
          `[data-item-id="${CSS.escape(`row-para-${paraId}`)}"]`,
        )
      }
    }
    if (!node) return
    const drift =
      node.getBoundingClientRect().top -
      el.getBoundingClientRect().top -
      anchor.offset
    if (drift !== 0) {
      selfScrollRef.current = true
      el.scrollTop += drift
    }
  }, [])

  /** **렌더 중에** 앵커를 잡는다 — 이 시점의 DOM 은 아직 이전 목록이다. */
  const prevRowsRef = useRef(rows)
  if (prevRowsRef.current !== rows) {
    prevRowsRef.current = rows
    if (!atNewest) captureAnchor()
  }

  /**
   * `unseen` 분모는 **행 배열이 아니라 `rawItems`** 다(D3-3). 사용자가 세고
   * 싶은 것은 "새 자막 N" 이지 "새 행 N" 이 아니다 — 문단 도착으로 행이
   * 재배치돼도 카운트가 흔들리면 안 된다. 자격은 통합 모드 경로와 글자
   * 그대로 같다(`kind === 'final' && seq > seenSeq`).
   */
  const maxSeq = useMemo(
    () =>
      rawItems.reduce(
        (max, it) => (it.seq !== null && it.seq > max ? it.seq : max),
        0,
      ),
    [rawItems],
  )
  const seenSeqRef = useRef(scrollState?.initial?.seenSeq ?? maxSeq)
  useEffect(() => {
    if (atNewest) {
      seenSeqRef.current = maxSeq
      setUnseen(0)
      return
    }
    const added = rawItems.filter(
      (it) =>
        it.kind === 'final' && it.seq !== null && it.seq > seenSeqRef.current,
    ).length
    if (added > 0) setUnseen(added)
  }, [maxSeq, atNewest, rawItems])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (atNewest) {
      stickToNewest()
      anchorRef.current = null
      return
    }
    restoreAnchor()
  })

  const prevOrderRef = useRef(order)
  useLayoutEffect(() => {
    if (prevOrderRef.current === order) return
    prevOrderRef.current = order
    anchorRef.current = null
    setAtNewest(true)
    stickToNewest()
  }, [order, stickToNewest])

  /**
   * `measureElement` 는 ResizeObserver 로 렌더 이후에 높이를 확정한다 —
   * 관측이 끝난 뒤 같은 앵커로 한 번 더 되돌린다(여기서 앵커를 다시 잡지
   * 않는 것이 요점이다: 다시 잡으면 밀린 자리가 새 기준이 된다).
   */
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (atNewest) {
        stickToNewest()
        return
      }
      restoreAnchor()
    })
    for (const node of el.querySelectorAll<HTMLElement>('[data-item-id]')) {
      observer.observe(node)
    }
    return () => observer.disconnect()
  }, [rows, atNewest, stickToNewest, restoreAnchor])

  useEffect(() => {
    stickToNewest()
  }, [stickToNewest])

  const liveScrollSnapshotRef = useRef({ atNewest, unseen })
  liveScrollSnapshotRef.current = { atNewest, unseen }

  if (scrollState) {
    scrollState.getSnapshot = () => {
      const snap = liveScrollSnapshotRef.current
      return {
        unseen: snap.unseen,
        seenSeq: seenSeqRef.current,
        anchorId: snap.atNewest ? null : (anchorRef.current?.id ?? null),
        anchorOffset: snap.atNewest ? 0 : (anchorRef.current?.offset ?? 0),
        atNewest: snap.atNewest,
      }
    }
  }

  useLayoutEffect(() => {
    const onSave = scrollState?.onSave
    if (!onSave) return undefined
    return () => {
      const snap = liveScrollSnapshotRef.current
      onSave({
        anchorId: anchorRef.current?.id ?? null,
        anchorOffset: anchorRef.current?.offset ?? 0,
        atNewest: snap.atNewest,
        unseen: snap.unseen,
        seenSeq: seenSeqRef.current,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const nextAtNewest = newestAtTop ? el.scrollTop < 48 : fromBottom < 48
    setAtNewest(nextAtNewest)
    if (selfScrollRef.current) {
      selfScrollRef.current = false
    } else {
      captureAnchor()
    }
    scrollState?.onSave({
      anchorId: nextAtNewest ? null : (anchorRef.current?.id ?? null),
      anchorOffset: nextAtNewest ? 0 : (anchorRef.current?.offset ?? 0),
      atNewest: nextAtNewest,
      unseen: liveScrollSnapshotRef.current.unseen,
      seenSeq: seenSeqRef.current,
    })

    const distanceFromOldest = newestAtTop ? fromBottom : el.scrollTop
    if (distanceFromOldest < 160 && onLoadMore && !loadingMore && !exhausted) {
      setLoadingMore(true)
      void onLoadMore()
        .then((more) => {
          if (!more) setExhausted(true)
        })
        .catch(() => setExhausted(true))
        .finally(() => setLoadingMore(false))
    }
  }, [
    onLoadMore,
    loadingMore,
    exhausted,
    captureAnchor,
    newestAtTop,
    scrollState,
  ])

  // 자막이 한 건도 없으면 대기 행 하나만 남는다 — 그때는 빈 상태를 그린다.
  const hasContent = rows.some(
    (r) => r.left.length > 0 || r.paragraph !== null,
  )
  if (!hasContent) {
    return (
      <div className="flex h-full items-center justify-center">
        <Empty
          icon={<MessagesSquare className="h-6 w-6" />}
          title={emptyTitle}
          description={emptyDescription}
        />
      </div>
    )
  }

  const cardProps = (
    item: FeedItem,
  ): React.ComponentProps<typeof CaptionCard> => ({
    item,
    showSource,
    veilSource,
    src,
    dst,
    ...(collapseInferred !== undefined ? { collapseInferred } : {}),
    ...(collapseAssistant !== undefined ? { collapseAssistant } : {}),
    ...(speakers ? { speakers } : {}),
    ...(onDelete ? { onDelete } : {}),
    ...(onRenameSpeaker ? { onRenameSpeaker } : {}),
    ...(room !== undefined ? { room } : {}),
    ...(viewerKey !== undefined ? { viewerKey } : {}),
  })

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <div className="relative h-full">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="region"
        aria-label="자막 — 실시간·긴호흡 나란히 보기"
        tabIndex={-1}
        className="split-rows feed-scroller h-full px-1"
        data-testid="split-rows"
        aria-live="polite"
        aria-relevant="additions"
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index]
            if (!row) return null
            const gutter =
              row.ts !== null
                ? clock(row.ts)
                : row.prev
                  ? '이전 회의'
                  : '대기 중'
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                // 앵커 좌표계는 **행 id** 다(D3-3) — 행 안의 카드가 다른 행으로
                // 옮겨가도 행 자체는 안정된 식별자를 유지한다.
                data-item-id={row.id}
                data-row-kind={row.kind}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <div
                  role="group"
                  aria-label={`${gutter} ${rowKindLabel(row)}`}
                  className={`split-row ${presentation ? 'split-row-presentation' : ''} ${
                    row.kind === 'pending' ? 'split-row-pending' : ''
                  } ${row.kind === 'orphan' ? 'split-row-orphan' : ''}`}
                  data-testid={`split-row-${row.kind}`}
                >
                  <div className="split-row-gutter">
                    <span className="font-mono tabular-nums">{gutter}</span>
                    {row.paragraph && (
                      <span className="tabular-nums">
                        {Math.round(row.paragraph.data.dur_s)}초 창
                      </span>
                    )}
                    {row.kind === 'orphan' && <span>문단 없음</span>}
                  </div>
                  {/* 좌측은 한 덩어리로 묶어 스크린리더가 건너뛸 수 있게 한다
                    (D3-2) — 문단 도착 시 이미 읽은 멤버의 재낭독은 이것으로
                    완전히 막히지는 않는다(잔여 위험 R2). */}
                  <div
                    className="split-row-left"
                    {...(row.kind === 'para'
                      ? {
                          role: 'group' as const,
                          'aria-label': `원본 ${row.left.length}건`,
                        }
                      : {})}
                  >
                    {row.left.length === 0 && row.kind === 'pending' && (
                      <div
                        className="split-row-placeholder"
                        data-testid="split-row-placeholder"
                      >
                        <LiveWaiting src={src} dst={dst} />
                      </div>
                    )}
                    {row.left.map((item) => (
                      <div
                        key={item.id}
                        data-card-id={item.id}
                        // 카드도 **앵커 좌표계에 참여한다**(D1-3). 대기 행에
                        // 있던 카드가 para 행으로 이동하는 것이 이 설계의
                        // 최대 위험(R1)이고, 그 이동을 잡으려면 앵커가 행이
                        // 아니라 **그 카드**여야 한다. 행만 앵커면 대기 행은
                        // 늘 존재하므로 앵커가 "안 사라지고" 폴백이 영원히
                        // 안 탄다 — 그러면 D1-3 의 방어선이 죽은 코드가 된다.
                        data-item-id={item.id}
                        className="split-row-card"
                      >
                        <CaptionCard {...cardProps(item)} />
                      </div>
                    ))}
                  </div>
                  <div className="split-row-right">
                    {row.paragraph ? (
                      <CaptionCard
                        {...cardProps(row.paragraph)}
                        members={row.left.filter(
                          (it): it is FinalItem => it.kind === 'final',
                        )}
                        // 행 좌측은 항상 시간 오름차순(D2-2, buildRows 계약) —
                        // members 도 같은 방향으로 고정해 CaptionCard 의
                        // order 기반 반전(DEF-096)을 타지 않게 한다.
                        order="newest-last"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {!atNewest && (
        <button
          type='button'
          data-testid="jump-to-newest"
          onClick={() => {
            setAtNewest(true)
            setUnseen(0)
            stickToNewest()
          }}
          className={`absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-[var(--color-primary)] px-4 py-1.5 text-(length:--text-xs-size) font-medium text-[var(--color-primary-foreground)] shadow-lg ${
            newestAtTop ? 'top-3' : 'bottom-3'
          }`}
        >
          {unseen > 0
            ? `새 자막 ${unseen > 99 ? '99+' : unseen}`
            : '최신 자막으로'}
        </button>
      )}
    </div>
  )
}
